import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

import { getDashboardSales } from "@/api/Dashboard/dashboard";
import Fetching from "@/components/Fetching/Fetching";
import { formatMilesDecimal, initialText, validRoles, formatShortNumber } from "@/services/Utils";

import classes from "./dashboard-sales.module.scss";

const DashboardSales = ({ filters }) => {
	const userRoles = JSON.parse(localStorage.getItem("roles"));
	const isManager = validRoles(userRoles, ["administrador", "gerente_agricola"]);

	const { estate_parent_id, estate_id, type_currency } = useSelector((state) => state.auth.filter);

	const { data, isLoading } = useQuery(["dashboard_sales", { ...filters, estate_parent_id, estate_id, type_currency }], () =>
		getDashboardSales({ ...filters, estate_parent_id, estate_id, type_currency }),
	);

	const [chartData, setChartData] = useState([]);
	const [totals, setTotals] = useState({ total: 0, applications: 0 });

	useEffect(() => {
		if (!data || !data.labels || !data.datasets?.length) return;

		const dataset = data.datasets[0];
		let transformed = [];

		data.labels.forEach((label, i) => {
			const name = label !== "SIN ASIGNAR" && label.split(" ").length > 1 ? initialText(label) : label;
			const value = dataset.data[i];

			transformed.push({
				label: name,
				value,
				fill: dataset.backgroundColor[i],
			});
		});

		setChartData(transformed);
		setTotals({
			total: Math.round(data.total) || 0,
			applications: Math.round(data.applications) || 0,
		});
	}, [data]);

	if (!isManager) return null;

	return (
		<>
			{isLoading ? (
				<Fetching topMargin="5em" bottomMargin="5em" />
			) : (
				<div className={classes.container}>
					<div className={classes.show_table}>
						<table className={classes.table}>
							<tbody>
								<tr>
									<td className={classes.first_td}>Compras</td>
									<td className={classes.last_td}>${formatMilesDecimal(totals.total)}</td>
								</tr>
								<tr>
									<td className={classes.first_td}>Aplicaciones</td>
									<td className={classes.last_td}>${formatMilesDecimal(totals.applications)}</td>
								</tr>
							</tbody>
						</table>
					</div>
					<div className={classes.graph}>
						<ResponsiveContainer width="100%" height="100%">
							<BarChart data={chartData} layout="vertical" margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
								<XAxis type="number" tickFormatter={formatShortNumber} tick={{ fontSize: 12 }} />
								<YAxis type="category" dataKey="label" width={100} tick={{ fontSize: 12 }} />
								<CartesianGrid strokeDasharray="3 3" horizontal={false} />
								<Tooltip
									content={({ active, payload, label }) => {
										if (active && payload && payload.length) {
											return (
												<div
													style={{
														backgroundColor: "#fff",
														border: "1px solid #ccc",
														padding: "8px",
														borderRadius: "6px",
													}}
												>
													<p
														style={{
															margin: 0,
															fontSize: "13px",
															fontWeight: 500,
															color: "#374151",
														}}
													>
														{label}: ${formatMilesDecimal(payload[0].value)}
													</p>
												</div>
											);
										}
										return null;
									}}
								/>
								<Bar dataKey="value" isAnimationActive={false}>
									{chartData.map((entry, index) => (
										<Cell key={`cell-${index}`} fill={entry.fill} />
									))}
								</Bar>
							</BarChart>
						</ResponsiveContainer>
					</div>
				</div>
			)}
		</>
	);
};

export default DashboardSales;
